import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthStorageKeys, AuthStorageService } from '../app/services/auth-storage.service';
import { HybridStorageKeys, HybridStorageService } from '../app/services/hybrid-storage.service';
import { environment as defaultEnvironment } from '../environments/environment';
import { environment as mobileStageEnvironment } from '../environments/environment.mobile-stage';

export interface RuntimeConfig {
  production: boolean;
  apiUrl: string;
  baseUrl?: string;
  /** Scholar parent portal web app base URL (no trailing slash). */
  parentPortalWebUrl: string;
  /** Public NHA marketing CMS base URL (no trailing slash) — prod www.nhaschools.com, non-prod nhaschools-np.nhatest.com. */
  cmsBaseUrl: string;
  /** URL of NHA's hosted College Readiness web-component bundle (defaults to the QA/prod myNHA host if unset). */
  collegeReadinessScriptUrl?: string;
  isDevelopment?: boolean;
  capgoChannel?: string;
  oktaConfig: any;
  oktaLoginTypes: any;
}

@Injectable({
  providedIn: 'root',
})
export class RuntimeConfigService {
  private configSubject = new BehaviorSubject<RuntimeConfig>(defaultEnvironment);
  public config$ = this.configSubject.asObservable();

  private isDemoModeSubject = new BehaviorSubject<boolean>(false);
  public isDemoMode$ = this.isDemoModeSubject.asObservable();

  private _initialized = false;
  private _initPromise: Promise<void> | null = null;

  constructor(
    private hybridStorageService: HybridStorageService,
    private authStorageService: AuthStorageService
  ) {}

  async initialize(): Promise<void> {
    if (this._initialized) return;
    if (this._initPromise) return this._initPromise;

    this._initPromise = this.initializeConfig();
    await this._initPromise;
    this._initialized = true;
  }

  private async initializeConfig(): Promise<void> {
    const isDemoMode = await this.hybridStorageService.getItem<boolean>(HybridStorageKeys.DemoMode);
    

    if (isDemoMode) {
    
      // Switch to mobile staging configuration without storing (already stored)
      this.configSubject.next(mobileStageEnvironment as RuntimeConfig);
      this.isDemoModeSubject.next(true);
    }
  }

  async enableDemoMode(store = true): Promise<void> {
    if (store) {
      await this.hybridStorageService.setItem(HybridStorageKeys.DemoMode, true);
    }

    // Switch to mobile staging configuration
    this.configSubject.next(mobileStageEnvironment as RuntimeConfig);
    this.isDemoModeSubject.next(true);
  }

  async disableDemoMode(): Promise<void> {
    await this.hybridStorageService.removeItem(HybridStorageKeys.DemoMode);

    // Switch back to default configuration
    this.configSubject.next(defaultEnvironment as RuntimeConfig);
    this.isDemoModeSubject.next(false);

  }

  get currentConfig(): RuntimeConfig {
    return this.configSubject.value;
  }

  get isDemoMode(): boolean {
    return this.isDemoModeSubject.value;
  }

  /**
   * A Scholar parent-portal URL carrying this session's login type. Scholar has no token of its
   * own on first arrival and would otherwise stop on its account-type picker; loginType tells it
   * which Okta configuration to use. silentSso tells Scholar to round-trip through Okta with
   * prompt=none, which signs the user in from the shared Okta session even when Scholar is not
   * same-site with the Okta domain (cross-site the embedded widget cannot see the session cookie).
   */
  portalUrl(path: string): string {
    const base = this.currentConfig.parentPortalWebUrl.replace(/\/$/, '');
    const suffix = path ? `/${path.replace(/^\//, '')}` : '';
    const loginType = this.portalLoginType();

    return loginType ? `${base}${suffix}?loginType=${loginType}&silentSso=true` : `${base}${suffix}`;
  }

  /** Scholar names its login types in lower case; School Connect stores them capitalised. */
  private portalLoginType(): string | null {
    switch (this.authStorageService.getItem(AuthStorageKeys.Okta_LoginType)) {
      case 'Parent':
        return 'parent';
      case 'Student':
        return 'student';
      case 'Staff':
        return 'staff';
      default:
        return null;
    }
  }
}